/**
 * Settings Page
 * Manage default assumptions and system configuration
 */

import React, { useState, useEffect } from 'react';
import {
  Gear,
  FloppyDisk,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { LoadingPage } from '../components/common';
import { settingsApi } from '../utils/api';
import { formatPercent } from '../utils/formatters';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';

export const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [originalDefaults, setOriginalDefaults] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const data = await settingsApi.getDefaults();
        setDefaults(data);
        setOriginalDefaults(data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsApi.updateDefaults(defaults);
      setOriginalDefaults(defaults);
      toast.success('Paramètres enregistrés');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Échec de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDefaults(originalDefaults);
    toast.info('Modifications annulées');
  };

  const updateField = (field, value) => {
    setDefaults(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const hasChanges = JSON.stringify(defaults) !== JSON.stringify(originalDefaults);

  if (loading) return <LoadingPage message="Chargement des paramètres..." />;

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
            <Gear size={20} className="text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Hypothèses par Défaut</h2>
            <p className="text-sm text-muted-foreground">
              Configurer les valeurs par défaut pour les nouveaux deals
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button 
              variant="outline" 
              onClick={handleReset}
              data-testid="reset-btn"
            >
              <ArrowCounterClockwise size={16} className="mr-1" />
              Annuler
            </Button>
          )}
          <Button 
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="bg-[#3A4A40] hover:bg-[#2E3B33]"
            data-testid="save-settings-btn"
          >
            <FloppyDisk size={16} className="mr-1" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Acquisition Defaults */}
      <div className="card-premium">
        <h3 className="font-semibold mb-6">Paramètres d'Acquisition</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label>Frais de Notaire par Défaut</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.default_notary_costs_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-notary-default"
              value={[defaults?.default_notary_costs_percent || 7.5]}
              onValueChange={([val]) => updateField('default_notary_costs_percent', val)}
              min={5}
              max={10}
              step={0.5}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Frais de notaire standard (typiquement 7-8%)
            </p>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Commission d'Agence par Défaut</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.default_agency_fee_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-agency-default"
              value={[defaults?.default_agency_fee_percent || 0]}
              onValueChange={([val]) => updateField('default_agency_fee_percent', val)}
              min={0}
              max={5}
              step={0.5}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Commission courtier/agence pour les deals sourcés
            </p>
          </div>
        </div>
      </div>

      {/* Renovation Defaults */}
      <div className="card-premium">
        <h3 className="font-semibold mb-6">Coûts de Rénovation par Défaut (par m²)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="reno-cosmetic">Cosmétique (€/m²)</Label>
            <Input
              id="reno-cosmetic"
              data-testid="input-reno-cosmetic"
              type="number"
              value={defaults?.renovation_cost_cosmetic_per_sqm || 0}
              onChange={(e) => updateField('renovation_cost_cosmetic_per_sqm', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
          <div>
            <Label htmlFor="reno-light">Légère (€/m²)</Label>
            <Input
              id="reno-light"
              data-testid="input-reno-light"
              type="number"
              value={defaults?.renovation_cost_light_per_sqm || 0}
              onChange={(e) => updateField('renovation_cost_light_per_sqm', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
          <div>
            <Label htmlFor="reno-medium">Moyenne (€/m²)</Label>
            <Input
              id="reno-medium"
              data-testid="input-reno-medium"
              type="number"
              value={defaults?.renovation_cost_medium_per_sqm || 0}
              onChange={(e) => updateField('renovation_cost_medium_per_sqm', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
          <div>
            <Label htmlFor="reno-heavy">Lourde (€/m²)</Label>
            <Input
              id="reno-heavy"
              data-testid="input-reno-heavy"
              type="number"
              value={defaults?.renovation_cost_heavy_per_sqm || 0}
              onChange={(e) => updateField('renovation_cost_heavy_per_sqm', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
          <div>
            <Label htmlFor="reno-gut">Rénovation Totale (€/m²)</Label>
            <Input
              id="reno-gut"
              data-testid="input-reno-gut"
              type="number"
              value={defaults?.renovation_cost_gut_per_sqm || 0}
              onChange={(e) => updateField('renovation_cost_gut_per_sqm', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <Label>Imprévu par Défaut</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.default_contingency_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-contingency-default"
              value={[defaults?.default_contingency_percent || 10]}
              onValueChange={([val]) => updateField('default_contingency_percent', val)}
              min={5}
              max={25}
              step={1}
            />
          </div>
        </div>
      </div>

      {/* Financing Defaults */}
      <div className="card-premium">
        <h3 className="font-semibold mb-6">Paramètres de Financement</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label>Taux d'Intérêt par Défaut</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.default_interest_rate)}
              </span>
            </div>
            <Slider
              data-testid="slider-interest-default"
              value={[defaults?.default_interest_rate || 4.5]}
              onValueChange={([val]) => updateField('default_interest_rate', val)}
              min={2}
              max={8}
              step={0.1}
            />
          </div>
          <div>
            <Label htmlFor="holding-period">Durée de Détention par Défaut (mois)</Label>
            <Input
              id="holding-period"
              data-testid="input-holding-period"
              type="number"
              value={defaults?.default_holding_period_months || 12}
              onChange={(e) => updateField('default_holding_period_months', e.target.value)}
              className="font-tabular mt-1"
            />
          </div>
        </div>
      </div>

      {/* Exit Defaults */}
      <div className="card-premium">
        <h3 className="font-semibold mb-6">Paramètres de Sortie</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label>Frais de Vente par Défaut</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.default_sales_fee_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-sales-fee-default"
              value={[defaults?.default_sales_fee_percent || 3]}
              onValueChange={([val]) => updateField('default_sales_fee_percent', val)}
              min={1}
              max={5}
              step={0.5}
            />
          </div>
        </div>
      </div>

      {/* Target Metrics */}
      <div className="card-premium">
        <h3 className="font-semibold mb-6">Objectifs de Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label>Rendement Cible</Label>
              <span className="text-sm font-tabular text-primary font-medium">
                {formatPercent(defaults?.target_roi_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-target-roi-default"
              value={[defaults?.target_roi_percent || 20]}
              onValueChange={([val]) => updateField('target_roi_percent', val)}
              min={10}
              max={40}
              step={1}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Utilisé pour les calculs d'offre maximale
            </p>
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <Label>Marge Brute Cible</Label>
              <span className="text-sm font-tabular">
                {formatPercent(defaults?.target_gross_margin_percent)}
              </span>
            </div>
            <Slider
              data-testid="slider-target-margin-default"
              value={[defaults?.target_gross_margin_percent || 15]}
              onValueChange={([val]) => updateField('target_gross_margin_percent', val)}
              min={5}
              max={30}
              step={1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
